// 1ヶ月
import { Actions, StageDetailUndeletableReason, StageKey } from 'src/types'

const DefaultMaxAge = 30 * 24 * 60 * 60

export type DefaultCookieOptionType = {
  maxAge: number | undefined
  path: string
  secure: boolean
  httpOnly: boolean
  sameSite: 'strict' | 'lax'
}

export const DefaultCookieOptions: DefaultCookieOptionType = {
  maxAge: DefaultMaxAge,
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  httpOnly: false,
  sameSite: 'strict',
} as const
// SSRで必要となるcookie
// roov.jpのような外部サイトから遷移した際に sameSite: strict だとcookieの値にアクセス出来なくなり、毎回ログインを求められるため lax を指定
export const RequiredSsrCookieOptions: DefaultCookieOptionType = {
  ...DefaultCookieOptions,
  sameSite: 'lax',
} as const

export const LocalKeyName = {
  AccessToken: 'accessToken',
  RefreshToken: 'refreshToken',
  Username: 'username',
  Email: 'email',
} as const

export const ErrorStatuses = {
  Validation: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  Expired: 406,
  AlreadyChecked: 409,
  ServerError: 500,
  BadGateway: 502,
} as const

export const Policy = {
  // プロジェクト
  createProject: 'createProject',
  updateProject: 'updateProject',
  deleteProject: 'deleteProject',
  assignProject: 'assignProject',
  readProjectReport: 'readProjectReport',
  // プロジェクト > 共通コンテンツ
  readCommonContent: 'readCommonContent',
  writeCommonContent: 'writeCommonContent',
  deleteCommonContent: 'deleteCommonContent',
  // プロジェクト > データ管理
  optOutCustomer: 'optOutCustomer',
  deleteCustomer: 'deleteCustomer',
  // ユーザー
  manageUser: 'manageUser',
  // 全権管理設定
  manageSecurity: 'manageSecurity',
  // ステージ管理
  manageCustomerStage: 'manageCustomerStage',
} as const

export const UsersPerPage = 100

export const CommonContentPageIcons = [
  {
    id: 0,
    iconPath: 'media-gallery/soft/line',
  },
  {
    id: 1,
    iconPath: 'madori/soft/line',
  },
  {
    id: 2,
    iconPath: 'appearance/soft/line',
  },
  {
    id: 3,
    iconPath: 'camera/soft/line',
  },
  {
    id: 4,
    iconPath: 'sunrise/soft/line',
  },
  {
    id: 5,
    iconPath: 'adjacent/soft/line',
  },
  {
    id: 6,
    iconPath: 'map/soft/line',
  },
  {
    id: 7,
    iconPath: 'pin/soft/line',
  },
  {
    id: 8,
    iconPath: 'calculator/soft/line',
  },
  {
    id: 9,
    iconPath: 'book/soft/line',
  },
  {
    id: 10,
    iconPath: 'chevron-up/soft/line',
  },
  {
    id: 11,
    iconPath: 'chevron-down/soft/line',
  },
  {
    id: 12,
    iconPath: 'chevron-left/soft/line',
  },
  {
    id: 13,
    iconPath: 'chevron-right/soft/line',
  },
  {
    id: 14,
    iconPath: 'arrow-up-circle/soft/line',
  },
  {
    id: 15,
    iconPath: 'arrow-down-circle/soft/line',
  },
  {
    id: 16,
    iconPath: 'arrow-left-circle/soft/line',
  },
  {
    id: 17,
    iconPath: 'arrow-right-circle/soft/line',
  },
  {
    id: 18,
    iconPath: 'folder/soft/line',
  },
  {
    id: 19,
    iconPath: 'book-open/soft/line',
  },
  {
    id: 20,
    iconPath: 'box/soft/line',
  },
  {
    id: 21,
    iconPath: 'download/soft/line',
  },
  {
    id: 22,
    iconPath: 'file-text/soft/line',
  },
  {
    id: 23,
    iconPath: 'gift/soft/line',
  },
  {
    id: 24,
    iconPath: 'heart/soft/line',
  },
  {
    id: 25,
    iconPath: 'home/soft/line',
  },
  {
    id: 26,
    iconPath: 'link2/soft/line',
  },
  {
    id: 27,
    iconPath: 'paperclip/soft/line',
  },
  {
    id: 28,
    iconPath: 'pen-tool/soft/line',
  },
  {
    id: 29,
    iconPath: 'plus-circle/soft/line',
  },
  {
    id: 30,
    iconPath: 'plan/soft/line',
  },
  {
    id: 31,
    iconPath: 'pdf/soft/line',
  },
  {
    id: 32,
    iconPath: 'qrcode/soft/line',
  },
  {
    id: 33,
    iconPath: 'questionnaire/soft/line',
  },
  {
    id: 34,
    iconPath: 'roov-walk/soft/line',
  },
  {
    id: 35,
    iconPath: 'schedule/soft/line',
  },
  {
    id: 36,
    iconPath: 'shopping-bag/soft/line',
  },
  {
    id: 37,
    iconPath: 'image/soft/line',
  },
  {
    id: 38,
    iconPath: 'star/soft/line',
  },
  {
    id: 39,
    iconPath: 'sun/soft/line',
  },
  {
    id: 40,
    iconPath: 'video/soft/line',
  },
  {
    id: 41,
    iconPath: 'play-circle/soft/line',
  },
  {
    id: 42,
    iconPath: 'thumbs-up/soft/line',
  },
  {
    id: 43,
    iconPath: 'trending-up/soft/line',
  },
  {
    id: 44,
    iconPath: 'smartphone/soft/line',
  },
  {
    id: 45,
    iconPath: 'type/soft/line',
  },
  {
    id: 46,
    iconPath: 'mail/soft/line',
  },
  {
    id: 47,
    iconPath: 'tag/soft/line',
  },
  {
    id: 48,
    iconPath: 'compass/soft/line',
  },
  {
    id: 49,
    iconPath: 'roov/soft/line',
  },
]

export const SidePanelId = 'sidePanel'

// ブロッキングローディング周りで利用するid/class
export const GlobalLoadingModalId = 'js-global-loading'
export const GlobalLoadingModalHiddenClass = 'hidden'
export const BodyLoadingClass = 'js-loading'

// 住戸テーブルに表示する行番号の初期値（1 - 100行目を表示する）
export const RoomTableInitialDisplayStartRowNumber = 1
export const RoomTableInitialDisplayEndRowNumber = 100
// 住戸テーブルに表示する最大住戸件数
export const RoomTableMaxDisplayRowCount = 200
// 住戸テーブルの表示範囲を増減させる際の行数
export const RoomTableDisplayRowIncrement = 50

export const ContractorHistoryActionMap: Record<Actions, string> = {
  iam_user_activation: '本人確認',
  iam_user_registration: '契約者登録',
  iam_user_deletion: '契約者情報削除',
  invitation_mail: '案内メール送信',
  agreement_request_mail: '同意依頼メール送信',
  select_registration: '回答の登録',
  select_agreement_cancellation: '同意取下げ',
  select_agreement: '同意',
  works_select_registration: '回答の登録',
  works_select_agreement_cancellation: '同意取下げ',
}

/** プロジェクトに登録可能な送信元名の最大文字数 */
export const EmailDisplayNameMaxLength = 64

/** ステージ */
export const StageNameMap: Record<StageKey, string> = {
  entry: 'エントリー',
  negotiation: '商談',
  reNegotiation: '再商談',
  requested: '要望',
  applied: '申込',
  contracted: '契約',
  cancel: 'キャンセル',
} as const

export const StageDetailDeleteErrorReasonMap: Record<StageDetailUndeletableReason, string> = {
  purchaseSearch: '保存済み検索条件',
  purchaserStage: '顧客のステージ',
  autoShare: 'シェアコンテンツの自動更新設定',
  reservationStageTransition: '予約管理のステージ遷移設定',
} as const
